import React from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';

import styles from './Accordion.module.css';
import dropdownContentTransitionStyles from './AccordionContents.Transition.module.css';
import KeyboardDownSVG from '../../assets/svg/ic_keyboard_arrow_down_24px.svg';

import { useAnalytics } from 'hooks/useAnalytics';
import RLPError from '../ErrorPage/RLPError';

const propTypes = {
  containingTab: PropTypes.string.isRequired,
  identifier: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string,
  isExpanded: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  spaceBetween: PropTypes.bool,
  additionalClasses: PropTypes.string,
};

/**
 * An Accordion represents a header which can be clicked  [<Title>  ^].
 * Clicking the header exposes the accordions content.
 * Accordion content can be anything contained within a single element.
 *
 * Styling:
 * -Clicking the accordions icon will rotate it 180deg pointing up/down.
 * -Contents will show/hide in correspondence with the arrow direction.
 */

function Accordion(props) {
  const { containingTab, identifier, title, isExpanded, onClick, children, spaceBetween, additionalClasses } = props;

  useAnalytics(`rlp-${containingTab}-reports`, isExpanded ? 'open-widget' : 'close-widget', identifier, isExpanded);

  const toggleIsExpandedState = (itemState) => {
    itemState.isExpanded = !itemState.isExpanded;
  };

  const handleClick = (event) => {
    try {
      event.preventDefault();
      onClick(containingTab, identifier, toggleIsExpandedState);
    } catch (err) {
      // create an RLP error so that the error condition is logged to Datadog
      // also log the error to the console
      new RLPError({ payload: props }, err).log();
    }
  };

  const handleKeystroke = (event) => {
    try {
      event.preventDefault();
      if (event.key.toLowerCase().indexOf('enter') === 0) {
        onClick(containingTab, identifier, toggleIsExpandedState);
      }
    } catch (err) {
      // create an RLP error so that the error condition is logged to Datadog
      // also log the error to the console
      new RLPError({ payload: props }, err).log();
    }
  };

  const ariaLabelledBy = props.ariaLabel ? null : `${containingTab}-${identifier}`;

  return (
    <div
      id={`${containingTab}-${identifier}-container`}
      className={`${styles['container']} ${spaceBetween ? styles['space-between'] : styles['line-between']}
                ${isExpanded ? styles['is-expanded'] : ''} ${additionalClasses}`}
    >
      <header className={styles['title']}>
        <h2 id={`${containingTab}-${identifier}`} className={styles['title-text']} onClick={handleClick}>
          {title}
        </h2>
        <span
          className={`${styles['icon']} ${styles['icon-arrow']} ${
            isExpanded ? styles['icon-up'] : styles['icon-down']
          }`}
          tabIndex={0}
          role="button"
          aria-labelledby={ariaLabelledBy}
          aria-label={props.ariaLabel}
          onClick={handleClick}
          onKeyUp={handleKeystroke}
        >
          <KeyboardDownSVG key="collapsed" />
        </span>
      </header>
      {
        <CSSTransition
          in={isExpanded}
          appear={true}
          unmountOnExit={true}
          classNames={dropdownContentTransitionStyles}
          timeout={dropdownContentTransitionStyles.transitionTime * 1000}
        >
          {children}
        </CSSTransition>
      }
    </div>
  );
}

Accordion.defaultProps = {
  spaceBetween: true,
  additionalClasses: '',
};

Accordion.propTypes = propTypes;

export default Accordion;
